import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import PersonalInfoForm from '../../components/PersonalInfoForm/PersonalInfoForm.component'
import DeleteOrChangeEmailSection from '../../components/DeleteOrChangeEmailSection/DeleteOrChangeEmailSection.component'
import { useMutation } from '@apollo/client'
import { GET_USER } from '../../graphql/queries/getUser.query'

import { USER_DATA } from '../../types/userData.interface'
import { UPDATE_USER } from '../../graphql/mutation/updateUser.mutation'
import Notification from '../../components/Notification/Notification.component'
import { useAuth } from '../../context/AuthContext/AuthContext'
import { useAuthenticatedQuery } from '../../hooks/useAuth'
import { BookingUpdateUserMutation, BookingUpdateUserMutationVariables } from 'parkdepot-shared/gql/graphql'
import { NotificationType } from '../../types/notification.enum'
import { FullWrapper, Title } from './PersonalInfo.style'

const PersonalInfo = () => {
    const { t } = useTranslation()
    const { token, setUser, user } = useAuth()
    const [name, setName] = useState('')
    const [lastName, setLastName] = useState('')
    const [email, setEmail] = useState('')
    const [saveLoading, setSaveLoading] = useState(false)
    const [showNotification, setShowNotification] = useState<NotificationType | null>(null)

    const { data: userFromApi, loading } = useAuthenticatedQuery<{
        get_user_data: USER_DATA
    }>({
        query: GET_USER
    })

    const [updateUser] = useMutation<BookingUpdateUserMutation, BookingUpdateUserMutationVariables>(UPDATE_USER, {
        context: {
            headers: {
                authorization: `Bearer ${token}`
            }
        }
    })

    useEffect(() => {
        if (user && name === '' && lastName === '' && email === '') {
            setName(user.first_name)
            setLastName(user.last_name)
            setEmail(user.email)
        }
    }, [user, email, lastName, name])

    useEffect(() => {
        if (userFromApi && userFromApi.get_user_data) {
            setName(userFromApi?.get_user_data.first_name)
            setLastName(userFromApi?.get_user_data.last_name)
            setEmail(userFromApi?.get_user_data.email)
            setUser(userFromApi?.get_user_data)
        }
    }, [userFromApi, setUser])

    const onChangeInformationClick = async () => {
        setShowNotification(null)
        setSaveLoading(true)
        try {
            await updateUser({
                variables: {
                    request: {
                        first_name: name,
                        last_name: lastName,
                        email
                    }
                }
            })
            setUser({
                ...(userFromApi?.get_user_data as USER_DATA),
                first_name: name,
                last_name: lastName
            })
            setShowNotification(NotificationType.POSITIVE)
        } catch (e) {
            setShowNotification(NotificationType.NEGATIVE)
        } finally {
            setSaveLoading(false)
        }
    }

    const onCancelClick = () => {
        setName(user?.first_name || '')
        setLastName(user?.last_name || '')
        setEmail(user?.email || '')
    }

    return (
        <FullWrapper>
            <Title>{t('profile.personalInformation')}</Title>
            <PersonalInfoForm
                name={name}
                lastName={lastName}
                loading={loading || saveLoading}
                setName={setName}
                setLastName={setLastName}
                onSubmit={onChangeInformationClick}
                onCancelClick={onCancelClick}
            />
            <DeleteOrChangeEmailSection email={email} />
            {showNotification && (
                <Notification type={showNotification}>
                    {t(
                        showNotification === NotificationType.POSITIVE
                            ? 'profile.successfulChanges'
                            : 'errors.errorOnSave'
                    )}
                </Notification>
            )}
        </FullWrapper>
    )
}

export default PersonalInfo
